import { useCallback } from 'react';
import useSWR from 'swr';

import { useTasksEnabled } from '~/components/tasks/useTasksEnabled';
import {
  tasksAssigneesList,
  tasksCreate,
  tasksList,
  tasksTidyNotesCreate,
} from '~/data/generated/endpoints/tasks/tasks';
import type { TaskCreateRequest, TaskDetail } from '~/data/generated/model';
import {
  TasksAssigneesListResponse,
  TasksListResponse,
} from '~/data/generated/zod/tasks/tasks.zod';
import {
  taskListKey,
  TASKS_KEY_PREFIX,
  type TaskScope,
  useRevalidateTasks,
} from '~/data/hooks/taskKeys';
import { parseApiResponse } from '~/data/parseApiResponse';

/** An empty scope lists every task the caller's company can see. */
export function useTasks(scope: TaskScope) {
  const enabled = useTasksEnabled();
  const { data, error, isLoading } = useSWR(
    enabled ? taskListKey(scope) : null,
    async ([, , propertyId, portfolioId, carId, portfolios, regions]) =>
      parseApiResponse(
        TasksListResponse,
        (
          await tasksList({
            property_id: propertyId ?? undefined,
            portfolio_id: portfolioId ?? undefined,
            car_id: carId ?? undefined,
            portfolio: portfolios ? portfolios.split(',') : undefined,
            region: regions ? regions.split(',') : undefined,
          })
        ).data,
        'TaskSummary',
      ),
  );
  return { tasks: data ?? [], error, isLoading };
}

export function useTaskAssignees(propertyId: string) {
  const enabled = useTasksEnabled();
  const { data, error, isLoading } = useSWR(
    enabled ? ([TASKS_KEY_PREFIX, 'assignees', propertyId] as const) : null,
    async ([, , id]) =>
      parseApiResponse(
        TasksAssigneesListResponse,
        (await tasksAssigneesList({ property_id: id })).data,
        'TaskPerson',
      ),
  );
  return { people: data ?? [], error, isLoading };
}

export function useCreateTask() {
  const revalidate = useRevalidateTasks();
  return useCallback(
    async (body: TaskCreateRequest): Promise<TaskDetail> => {
      const { data } = await tasksCreate(body);
      await revalidate();
      return data;
    },
    [revalidate],
  );
}

export async function tidyNotes(text: string): Promise<string> {
  const { data } = await tasksTidyNotesCreate({ text });
  return data.text;
}
